import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Modal } from "react-bootstrap";
import { Modal as MuiModal, Box } from "@mui/material";
import api from "../../../../api/api";
import "../../../../components/table.css";
import EditImg from "../../../../image/Edit.svg";
import ViewImg from "../../../../image/View.svg";
import VerifyImg from "../../../../image/verify.png";
import Pagination from "../../../../components/Pagination";
import CancelIcon from "@mui/icons-material/Cancel";
import Button from "../../../../components/Button";
import { useAuth } from "../../../../context/AuthContext";
import Breadcrumbs from "../../../../components/Breadcrumbs";

const ListOfGSTUpdateRequests = () => {
  const { refreshUser } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  // Modal states
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [selectedUserId, setSelectedUserId] = useState(null);

  useEffect(() => {
    const fetchRequests = async () => {
      try {
        const response = await api.get("/gst-update-requests");
        console.log("response", response);

        // Transform backend data to match frontend expectations
        const transformedRequests = Array.isArray(response.data.gstUpdateRequests)
          ? response.data.gstUpdateRequests.map(request => ({
              id: request.user_id, // Assuming user_id is available
              user_id: request.user_id,
              full_name: request.full_name,
              company_name: request.full_name, // Use full_name as company_name
              current_gst_number: request.gst_number,
              new_gst_number: request.updatedGstNo,
              document_url: request.file_url,
              status: request.profile_status === 'GSTUpdatePending' ? 'Pending' : 'Processed'
            }))
          : [];

        console.log("Transformed requests:", transformedRequests);

        setRequests(transformedRequests);
      } catch (error) {
        console.error("Failed to fetch GST update requests:", error);
        setRequests([]);
      } finally {
        setLoading(false);
      }
    };

    fetchRequests();
  }, []);

  // Pagination calculations
  const totalPages = Math.ceil(requests.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = requests.slice(startIndex, startIndex + itemsPerPage);

  const goToPage = (page) => {
    if (page < 1) page = 1;
    if (page > totalPages) page = totalPages;
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (num) => {
    setItemsPerPage(num);
    setCurrentPage(1);
  };

  const openVerifyModal = (userId) => {
    console.log("Opening verify modal for userId:", userId);
    setSelectedUserId(userId);
    setShowVerifyModal(true);
  };

  const openRejectModal = (userId) => {
    setSelectedUserId(userId);
    setShowRejectModal(true);
  };

  const closeModals = () => {
    setShowVerifyModal(false);
    setShowRejectModal(false);
    setSelectedUserId(null);
  };

  const handleVerify = async () => {
    try {
      console.log("Verifying GST update for userId:", selectedUserId);
      await api.put(`/verify-gst-update/${selectedUserId}`);
      setSuccessMessage("GST update request verified successfully!");
      setShowSuccessModal(true);
      // Refresh the list after verification
      const response = await api.get("/gst-update-requests");
      const transformedRequests = Array.isArray(response.data.gstUpdateRequests)
        ? response.data.gstUpdateRequests.map(request => ({
            id: request.user_id,
            full_name: request.full_name,
            company_name: request.full_name,
            current_gst_number: request.gst_number,
            new_gst_number: request.updatedGstNo,
            document_url: request.file_url,
            status: request.profile_status === 'GSTUpdatePending' ? 'Pending' : 'Processed'
          }))
        : [];
      setRequests(transformedRequests);
      // Refresh user data to update dashboard cards immediately
      await refreshUser();
      closeModals();
    } catch (error) {
      console.error("Failed to verify GST update request:", error);
    }
  };

  const handleReject = async () => {
    try {
      await api.put(`/gst-update-requests/${selectedUserId}/reject`);
      setSuccessMessage("GST update request rejected successfully!");
      setShowSuccessModal(true);
      // Refresh the list after rejection
      const response = await api.get("/gst-update-requests");
      const transformedRequests = Array.isArray(response.data.gstUpdateRequests)
        ? response.data.gstUpdateRequests.map(request => ({
            id: request.user_id,
            full_name: request.full_name,
            company_name: request.full_name,
            current_gst_number: request.gst_number,
            new_gst_number: request.updatedGstNo,
            document_url: request.file_url,
            status: request.profile_status === 'GSTUpdatePending' ? 'Pending' : 'Processed'
          }))
        : [];
      setRequests(transformedRequests);
      // Refresh user data to update dashboard cards immediately
      await refreshUser();
      closeModals();
    } catch (error) {
      console.error("Failed to reject GST update request:", error);
    }
  };

  return (
    <>
    <Breadcrumbs />
      <div className="d-flex align-items-center mb-3"></div>

      {loading ? (
        <p>Loading GST update requests...</p>
      ) : (
        <>
            <div
            className={`table-responsive table-fixed-header overflow-auto  ${
              itemsPerPage >= 20 ? "mb-80" : itemsPerPage > 11 ? "mb-3" : ""
            }`}
          >
            <table
              className={`table table-bordered table-hover  ${
                itemsPerPage >= 20 ? "mb-3" : itemsPerPage < 11 ? "mb-0" : ""
              }`}
            >
              <thead className="table-light">
                <tr>
                  <th>Client Name</th>
                  <th>Current GST Number</th>
                  <th>Updated GST Number</th>
                  <th>New GST Document</th>
                  <th style={{ width: "200px" }} className="text-center">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {currentItems.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="text-center">
                      No GST update requests found
                    </td>
                  </tr>
                ) : (
                  currentItems.map((request) => (
                    <tr key={request.id}>
                      <td>{request.company_name || request.full_name}</td>
                      <td>{request.current_gst_number}</td>
                      <td>{request.new_gst_number}</td>
                      <td className="text-center">
                        {request.document_url ? (
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => window.open(request.document_url, '_blank')}
                            title="View Document"
                          >
                            <img src={ViewImg} alt="View" />
                          </button>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td className="text-center">
                        <div className="d-flex gap-2 justify-content-center">
                          {request.status === "Pending" && (
                            <>
                              <button
                                className="btn btn-sm btn-outline-success"
                                onClick={() => openVerifyModal(request.user_id)}
                                title="Verify Request"
                              >
                                <img src={VerifyImg} alt="Verify" />
                              </button>
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => openRejectModal(request.user_id)}
                                title="Reject Request"
                              >
                                <CancelIcon fontSize="small" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={goToPage}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={handleItemsPerPageChange}
          />
        </>
      )}

      {/* Verify Modal */}
      <Modal show={showVerifyModal} onHide={closeModals} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Verification</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to verify this GST update request?</p>
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-secondary" onClick={closeModals}>
            Cancel
          </button>
          <button className="btn btn-success" onClick={handleVerify}>
            Verify
          </button>
        </Modal.Footer>
      </Modal>

      {/* Reject Modal */}
      <Modal show={showRejectModal} onHide={closeModals} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Rejection</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to reject this GST update request?</p>
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-secondary" onClick={closeModals}>
            Cancel
          </button>
          <button className="btn btn-danger" onClick={handleReject}>
            Reject
          </button>
        </Modal.Footer>
      </Modal>

      {/* Success Modal */}
      <MuiModal open={showSuccessModal} onClose={() => setShowSuccessModal(false)}>
        <Box className="modal-box" style={{ height: "200px" }}>
          <h6 className="fw-bold">Success</h6>
          <p>{successMessage}</p>
          <div className="row mt-5 justify-content-center">
            <div className="col-6">
              <Button variant="primary" btncls="w-100" onClick={() => setShowSuccessModal(false)}>
                OK
              </Button>
            </div>
          </div>
        </Box>
      </MuiModal>
    </>
  );
};

export default ListOfGSTUpdateRequests;
